// MACHINERY V2 (Stroje) — the katalog's mobile FILTER SHEET (M2.16c).
//
// The katalog counterpart of fleet/FleetFilterSheet.tsx, built to the same
// shape on purpose: the CHROME (radius 16 top, 36x4 handle, rgba(15,23,30,0.32)
// + blur(2) scrim, pinned header/footer with a scrolling body, the portal rule,
// the propagation guards) is mobile/SheetV2.tsx and is shared; what is here is
// the katalog's own header action, footer and content.
//
// >>> THE TWO FOOTER VERBS ARE NOT THE SAME BUTTON — the fleet's semantics,
// kept. <<< "Zavriet" DISCARDS the pending selection; the primary APPLIES it.
// Esc and the backdrop take the Zavriet path, because Radix routes all three
// through the same `onOpenChange(false)`; nothing needs discarding explicitly,
// since the next open re-seeds from applied state (useCatalogFilterDraft.ts).
//
// >>> WHERE IT DIVERGES FROM THE FLEET, AND WHY. <<<
//   · ALL EIGHT SECTIONS ARE STAGED. The fleet splits staged facets from live
//     mode controls; the katalog has no mode controls, so there is no split and
//     no "applies immediately" note to print.
//   · THE PRIMARY CARRIES NO COUNT. The fleet previews "Zobrazit n strojov" from
//     an in-memory array; the katalog's total comes from the server, so there is
//     no honest n to print before the request the staging exists to avoid. Full
//     reasoning in useCatalogFilterDraft.ts's header. Do NOT wire the current
//     total in — it describes the state the user is leaving.
//
// >>> RULE (d): THE SHEET HOLDS USER WORK AND NEVER DISCARDS IT BY ACCIDENT.
// <<< A pending eight-section selection is user work. It is written only by a
// user gesture and by the open transition, so a re-render, the browse hook's
// stale-response guard firing, or a failed request all leave it untouched.
//
// >>> AND THE DRAFT ARRIVES AS A PROP — IT IS NOT CREATED HERE (review finding
// F4). <<< This component is mounted by CatalogMobileChrome, which CatalogShell
// unmounts the instant a tablet crosses 1280, so a hook called here died with
// it and took the staged selection with it. `useCatalogFilterDraft` is now
// called by CatalogBrowseV2, above every breakpoint branch — the same shape
// CatalogAddDialogV2 already used for its form state, and for the same reason.
// The `open` flag is the page's for the same reason: a draft that survives the
// rotation while the sheet silently closes is the same lost gesture.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import SheetV2 from "../mobile/SheetV2";
import { SheetTextAction } from "../mobile/sheetAtoms";
import CatalogSheetSections from "./catalogSheetSections";
import { CatalogFacetModel } from "./catalogFacets";
import { CatalogFilterDraftApi } from "./useCatalogFilterDraft";
import useCatalogBrowse from "./useCatalogBrowse";

export interface CatalogFilterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  c: ReturnType<typeof useCatalogBrowse>;
  facets: CatalogFacetModel;
  /** Created by CatalogBrowseV2, above the 1280 branch — see the header. */
  api: CatalogFilterDraftApi;
}

const CatalogFilterSheet: React.FC<CatalogFilterSheetProps> = ({
  open,
  onOpenChange,
  c,
  facets,
  api,
}) => {
  const { translate } = useTranslation("myMachinery");

  const apply = () => {
    api.commit();
    onOpenChange(false);
  };

  return (
    <SheetV2
      open={open}
      onOpenChange={onOpenChange}
      title={translate("myMachinery:filterBy")}
      headerAction={
        <SheetTextAction
          label={translate("myMachinery:v2.mobile.clearAll")}
          onClick={api.clearAll}
        />
      }
      footer={
        <>
          <button
            type="button"
            className="btn"
            onClick={() => onOpenChange(false)}
            style={{
              flex: 1,
              height: 46,
              // >= 44px BOTH axes. flex:1 already makes this far wider on any
              // phone; the floor is the law, not a prediction.
              minWidth: 44,
              justifyContent: "center",
              borderRadius: 10,
              fontSize: 13.5,
            }}
          >
            {translate("myMachinery:close")}
          </button>
          <button
            type="button"
            className="btn btn-mod"
            onClick={apply}
            style={{
              flex: 2,
              height: 46,
              minWidth: 44,
              justifyContent: "center",
              borderRadius: 10,
              fontSize: 13.5,
            }}
          >
            {translate("myMachinery:v2.catalog.applyFilters")}
          </button>
        </>
      }
    >
      <CatalogSheetSections api={api} c={c} facets={facets} />
    </SheetV2>
  );
};

export default CatalogFilterSheet;
